import { etiquetaEstado } from "@/lib/estado-labels";
import { Badge } from "./Badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "./Tooltip";

/**
 * Mapa de color por estado del flujo de una solicitud · portado 1:1 de
 * `components/ui/estado-badge.tsx` (Mantine). Los nombres de color de
 * Mantine ya estaban re-mapeados en `mantine-theme.ts` a las rampas de la
 * paleta oficial (`teal/green→exito`, `red→peligro`, `orange→advertencia`,
 * `blue→info`, `indigo→azulProfundo`, `cyan→azulCielo`) — acá se usan esos
 * mismos nombres de rampa directamente, sin capa de traducción.
 */
const COLORES: Record<string, string> = {
  Iniciada: "slate",
  TipoSeleccionado: "slate",
  SubtipoSeleccionado: "slate",
  ContratoAnualizadoSeleccionado: "slate",
  OrdenDeCompraSeleccionada: "slate",
  CfdiPendiente: "slate",
  CfdiCargado: "info",
  EnValidacionReglas: "info",
  ReglasRechazadas: "peligro",
  EnValidacionFiscal: "info",
  Validado: "exito",
  RechazadoFiscal: "peligro",
  SuspendidaFiscal: "advertencia",
  CapturandoDatos: "slate",
  SeleccionandoProveedor: "slate",
  Prorrateando: "slate",
  ProvisionalmenteListo: "exito",
  AdjuntandoDocumentos: "info",
  ListoParaAutorizacion: "azulProfundo",
  EnRevisionDocumental: "info",
  EnAutorizacionNivel1: "azulProfundo",
  EnAutorizacionNivelN: "azulProfundo",
  RechazadaParaCorreccion: "advertencia",
  Autorizada: "exito",
  EnColaContabilizacion: "azulCielo",
  PolizaGenerada: "azulCielo",
  EnviadaAContabilizacion: "exito",
  DadaDeBajaPorAutorizador: "peligro",
  CanceladaPorSolicitante: "peligro",
  DescartadaPorPlazoVencido: "peligro",
};

export function colorDeEstado(estado: string): string {
  return COLORES[estado] ?? "slate";
}

export interface EstadoBadgeProps {
  estado: string;
  size?: "xs" | "sm" | "md";
  conTooltip?: boolean;
  label?: string;
  /** Texto del tooltip cuando difiere del visible (ej. label compacto en tabla + nombre completo al hover). */
  tooltipLabel?: string;
  /** Pasa `max-w-full` (con un padre `min-w-0`) para que el badge TRUNQUE en celdas angostas en vez de desbordarse sobre las columnas vecinas. */
  className?: string;
  /** Override del color · por default sale del mapa semántico por estado. */
  color?: string;
  /** Override de la variante · por default `"light"` (pastel). */
  variant?: "light" | "filled" | "outline";
}

/** Reemplazo de `components/ui/estado-badge.tsx` · badge de estado del flujo de una solicitud (~28 estados). */
export function EstadoBadge({ estado, size = "sm", conTooltip = false, label, tooltipLabel, className, color, variant = "light" }: EstadoBadgeProps) {
  const texto = label ?? etiquetaEstado(estado);
  const badge = (
    <Badge color={color ?? colorDeEstado(estado)} variant={variant} size={size} className={className}>
      {/* El span interno con truncate permite elipsis dentro del inline-flex del Badge
          (sin ancho restringido no cambia nada · el badge sigue shrink-to-fit). */}
      <span className="truncate">{texto}</span>
    </Badge>
  );
  if (!conTooltip) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>{tooltipLabel ?? texto}</TooltipContent>
    </Tooltip>
  );
}
